import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Sparkles, Cpu, ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Welcome popup shown after login. Auto-closes after 10 seconds.
 * Triggered by AppShell when a flag is set in sessionStorage.
 */
export function WelcomeDialog() {
  const [open, setOpen] = useState(false);
  const [countdown, setCountdown] = useState(10);

  useEffect(() => {
    const should = sessionStorage.getItem("acas_show_welcome");
    if (should === "1") {
      setOpen(true);
      sessionStorage.removeItem("acas_show_welcome");
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setCountdown(10);
    const tick = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(tick);
          setOpen(false);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="welcome-dialog"
        className="max-w-2xl border-0 p-0 overflow-hidden"
      >
        <VisuallyHidden>
          <DialogTitle>Welcome to Traceroot — Unified Loan Origination System</DialogTitle>
          <DialogDescription>Developed by Bharat Cores Solutions Private Limited. This dialog auto-closes in 10 seconds.</DialogDescription>
        </VisuallyHidden>
        <div className="relative">
          {/* Decorative gradient header (within 20% rule) */}
          <div className="relative h-40 bg-gradient-to-br from-primary via-primary to-accent overflow-hidden">
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.5) 0, transparent 40%), radial-gradient(circle at 80% 60%, rgba(255,255,255,0.35) 0, transparent 40%)" }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-primary-foreground">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Sparkles className="h-7 w-7" />
                  <Cpu className="h-7 w-7" />
                  <ShieldCheck className="h-7 w-7" />
                </div>
                <h2 className="heading text-2xl font-bold tracking-tight">Traceroot</h2>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="welcome-close-button"
              className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/15 hover:bg-white/25 text-primary-foreground flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Message */}
          <div className="px-7 py-6 text-center">
            <p className="heading text-xl font-semibold leading-snug mb-3">
              Welcome to <span className="text-accent">Traceroot</span><br />
              Unified Loan Origination System
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">
              Developed by{" "}
              <span className="font-semibold text-foreground">
                Bharat Cores Solutions Private Limited
              </span>
            </p>
            <div className="grid grid-cols-3 gap-3 mt-5 max-w-md mx-auto">
              <div className="rounded-md border border-border bg-card p-2.5 text-center">
                <Sparkles className="h-4 w-4 text-accent mx-auto mb-1" />
                <div className="text-[11px] font-medium">Auto Engine</div>
              </div>
              <div className="rounded-md border border-border bg-card p-2.5 text-center">
                <Cpu className="h-4 w-4 text-primary mx-auto mb-1" />
                <div className="text-[11px] font-medium">CMA / CRA</div>
              </div>
              <div className="rounded-md border border-border bg-card p-2.5 text-center">
                <ShieldCheck className="h-4 w-4 text-success mx-auto mb-1" />
                <div className="text-[11px] font-medium">AES-GCM</div>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-between gap-3">
              <p className="text-[11px] text-muted-foreground">
                Auto-closes in <span className="mono font-medium text-foreground" data-testid="welcome-countdown">{countdown}</span>s
              </p>
              <Button
                size="sm"
                onClick={() => setOpen(false)}
                data-testid="welcome-dismiss-button"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default WelcomeDialog;
